// AdminChewTickets — office view of every request from the Chew Flats hub
// link. Shows unit, tenant, AI-drafted WO, photos, proposed entry windows,
// current status, message thread. Admin can accept a window (schedules the
// visit + emails tenant), send a reply, and jump to the linked WO in Jobs.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Envelope, Clock, MapPin, ArrowRight, PaperPlaneTilt, GearSix, Warning, Link as LinkIcon, Copy, LockKey } from "@phosphor-icons/react";
import { AdminShell as Layout } from "@/components/Layout";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminChewTickets() {
  const nav = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [showPermitCreate, setShowPermitCreate] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const r = await api.get(`/chew/hub/tickets${q}`);
      setTickets(r.data.tickets || []);
    } catch (e) { toast.error("Could not load Chew Flats tickets"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [statusFilter]);

  const stats = useMemo(() => {
    const s = { total: tickets.length, submitted: 0, scheduled: 0, urgent: 0 };
    tickets.forEach(t => {
      if (t.status === "submitted") s.submitted++;
      if (t.status === "scheduled") s.scheduled++;
      if (t.priority === "urgent") s.urgent++;
    });
    return s;
  }, [tickets]);

  const [refSearch, setRefSearch] = useState("");
  const [refBusy, setRefBusy]     = useState(false);

  async function jumpByRef(e) {
    e?.preventDefault();
    const val = refSearch.trim().toUpperCase();
    if (val.length < 4) { toast.error("Enter a full reference code"); return; }
    setRefBusy(true);
    try {
      const r = await api.post("/public/chew/hub/lookup-by-ref", { ref: val });
      const hit = tickets.find(t => t.id === r.data.ticket_id);
      if (hit) { setSelected(hit); setRefSearch(""); return; }
      // Not in current filter — broaden, reload, then open
      setStatusFilter("");
      const rl = await api.get("/chew/hub/tickets");
      const full = rl.data.tickets || [];
      setTickets(full);
      const h2 = full.find(x => x.id === r.data.ticket_id);
      if (h2) { setSelected(h2); setRefSearch(""); }
      else toast.error("Ticket found but not visible with current role.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Not found"));
    } finally { setRefBusy(false); }
  }

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="admin-chew-tickets">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-500">Chew Flats · LaSalle Pilot</div>
            <h1 className="text-2xl font-black mt-1">Tenant Reporter Inbox</h1>
            <p className="text-sm text-slate-500 mt-1">One link that goes to every student at 5346 Chew Ave. Post the QR in the mailroom.</p>
            <div className="mt-2 text-xs">
              <span className="text-slate-500">Public link:</span>{" "}
              <a href="/report/chew-flats" target="_blank" rel="noreferrer" className="font-mono text-cyan-600 underline" data-testid="chew-tickets-public-link">/report/chew-flats</a>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={()=>setShowPermitCreate(true)} className="text-xs font-black bg-cyan-600 text-white rounded-lg px-3 py-2 flex items-center gap-1.5 hover:bg-cyan-700" data-testid="chew-tickets-new-permit-btn"><LinkIcon size={14}/> New entry-permission link</button>
            <button onClick={()=>setShowConfig(true)} className="text-xs font-bold bg-slate-100 rounded-lg px-3 py-2 flex items-center gap-1.5 hover:bg-slate-200" data-testid="chew-tickets-config-btn"><GearSix size={14}/> Notify config</button>
          </div>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Stat n={stats.total} label="Total requests"/>
          <Stat n={stats.submitted} label="Awaiting scheduling" color="text-orange-600"/>
          <Stat n={stats.scheduled} label="Scheduled" color="text-emerald-600"/>
          <Stat n={stats.urgent} label="Urgent" color="text-red-600"/>
        </div>

        {/* Filter + Ref-code jump */}
        <div className="flex gap-2 mb-4 text-xs font-bold flex-wrap items-center">
          {["", "submitted", "scheduled", "draft"].map(s => (
            <button key={s} onClick={()=>setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full ${statusFilter===s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}
              data-testid={`chew-tickets-filter-${s || "all"}`}>
              {s || "All"}
            </button>
          ))}
          <form onSubmit={jumpByRef} className="ml-auto flex items-center gap-1 bg-white border-2 border-slate-200 rounded-full pl-3 pr-1 py-0.5 focus-within:border-cyan-500" data-testid="chew-tickets-ref-search">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Ref</span>
            <input value={refSearch} onChange={e => setRefSearch(e.target.value.toUpperCase())}
              placeholder="CTOEFG"
              autoComplete="off" maxLength={12}
              data-testid="chew-tickets-ref-input"
              className="font-mono text-xs uppercase tracking-widest bg-transparent outline-none w-[110px]"/>
            <button type="submit" disabled={refBusy || refSearch.trim().length < 4}
              data-testid="chew-tickets-ref-jump-btn"
              className="rounded-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-300 text-white h-6 w-6 flex items-center justify-center">
              <ArrowRight size={12} weight="bold"/>
            </button>
          </form>
        </div>

        {loading && <div className="text-sm text-slate-400">Loading…</div>}
        {!loading && !tickets.length && (
          <div className="bg-slate-50 rounded-2xl p-10 text-center text-slate-500">No tickets yet. Share the link above with students or LaSalle housing.</div>
        )}

        <div className="space-y-3">
          {tickets.map(t => (
            <TicketCard key={t.id} t={t} onOpen={()=>setSelected(t)}/>
          ))}
        </div>
      </div>

      {selected && <TicketDrawer ticket={selected} onClose={()=>{ setSelected(null); load(); }} nav={nav}/>}
      {showConfig && <NotifyConfigModal onClose={()=>setShowConfig(false)}/>}
      {showPermitCreate && <NewPermitModal onClose={()=>setShowPermitCreate(false)} onCreated={load}/>}
    </Layout>
  );
}

function Stat({ n, label, color = "text-slate-900" }) {
  return (
    <div className="bg-white rounded-xl p-3 shadow-sm">
      <div className={`text-2xl font-black ${color}`}>{n}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    </div>
  );
}

function TicketCard({ t, onOpen }) {
  const prio = { urgent: "bg-red-100 text-red-700", high: "bg-orange-100 text-orange-700", normal: "bg-cyan-50 text-cyan-700" }[t.priority] || "bg-slate-100 text-slate-600";
  const status = { submitted: "bg-orange-500", scheduled: "bg-emerald-500", draft: "bg-slate-300" }[t.status] || "bg-slate-300";
  const contactChip = {
    email: { emoji: "📧", label: "Email on file" },
    phone: { emoji: "📱", label: "SMS on file" },
    none:  { emoji: "🔗", label: "Status link only" },
  }[t.contact_kind || "none"];
  return (
    <button onClick={onOpen} className="w-full text-left bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition flex gap-4" data-testid={`chew-ticket-card-${t.id}`}>
      <div className="w-1 rounded-full self-stretch" style={{ background: t.priority === "urgent" ? "#dc2626" : "#00C4CC" }}/>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-block ${prio} text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full`}>{t.priority}</span>
          <span className="text-[11px] font-bold text-slate-500">Unit {t.unit}</span>
          <span className={`inline-block ${status} w-2 h-2 rounded-full`}/>
          <span className="text-[11px] font-bold text-slate-500 capitalize">{t.status}</span>
          <span className="ml-auto text-[10px] text-slate-400 font-mono">{t.ref_code}</span>
        </div>
        <div className="font-black text-slate-900 mt-1 truncate">{t.subject}</div>
        <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>
        <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500 flex-wrap">
          <span className="font-bold text-slate-700">Resident {t.initials || "?"}</span>
          <span>·</span>
          <span title={contactChip.label}>{contactChip.emoji} {contactChip.label}</span>
          {t.photos?.length ? <span>· {t.photos.length} photo{t.photos.length===1?"":"s"}</span> : null}
          {t.entry_windows?.length ? <span>· {t.entry_windows.length} window{t.entry_windows.length===1?"":"s"}</span> : null}
          {t.messages?.length ? <span>· {t.messages.length} msg</span> : null}
          {t.permission_token && <span className="ml-1 text-cyan-700 font-black">· permit link{t.permission_granted ? " ✓" : ""}</span>}
        </div>
      </div>
      <ArrowRight size={18} className="text-slate-300 self-center"/>
    </button>
  );
}

function TicketDrawer({ ticket, onClose, nav }) {
  const [t, setT] = useState(ticket);
  const [msgText, setMsgText] = useState("");
  const [busy, setBusy] = useState(false);
  const [adminWin, setAdminWin] = useState({ start: "", end: "", label: "", note: "", hard_appointment: false });

  const BASE = process.env.REACT_APP_BACKEND_URL;

  async function refreshFromPermit() {
    // Re-fetch via public endpoint if this ticket has a permission_token.
    if (!t.permission_token) return;
    try {
      const r = await api.get(`/public/chew/permit/${t.permission_token}`);
      setT(prev => ({
        ...prev,
        accepted_window: r.data.accepted_window,
        entry_windows: r.data.tenant_proposed_windows || prev.entry_windows,
        admin_proposed_windows: r.data.admin_proposed_windows || [],
        messages: r.data.messages || prev.messages,
        status: r.data.status || prev.status,
        permission_granted: r.data.already_granted,
      }));
    } catch {}
  }

  async function acceptWindow(idx, note = "") {
    setBusy(true);
    try {
      const r = await api.post(`/chew/hub/tickets/${t.id}/accept-window`, { window_index: idx, note });
      toast.success("Entry window confirmed — tenant notified");
      setT({ ...t, accepted_window: r.data.accepted_window, status: "scheduled" });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  }
  async function sendMsg() {
    if (!msgText.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/chew/hub/tickets/${t.id}/message`, { text: msgText });
      setT({ ...t, messages: [...(t.messages||[]), r.data.message] });
      setMsgText("");
    } catch (e) { toast.error("Message failed"); }
    finally { setBusy(false); }
  }
  async function proposeAdminWindow() {
    if (!adminWin.start.trim() || !adminWin.end.trim()) { toast.error("Add start and end times"); return; }
    if (!t.permission_token) { toast.error("This ticket has no permission link"); return; }
    setBusy(true);
    try {
      await api.post(`/chew/hub/permit/${t.permission_token}/admin-window`, adminWin);
      toast.success(adminWin.hard_appointment ? "Hard appointment set — student notified" : "Window proposed — student will see it");
      setAdminWin({ start: "", end: "", label: "", note: "", hard_appointment: false });
      await refreshFromPermit();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  }
  async function copyPermitLink() {
    if (!t.permission_url) return;
    try {
      await navigator.clipboard.writeText(t.permission_url);
      toast.success("Link copied — paste it into your SMS/email");
    } catch { toast.error("Could not copy"); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="chew-ticket-drawer">
        {/* Header */}
        <div className="p-5 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2 text-[10px] uppercase font-black tracking-widest text-slate-500">
            <span>Unit {t.unit}</span><span>·</span><span className="font-mono">{t.ref_code}</span>
            <span className="ml-auto capitalize bg-slate-100 px-2 py-0.5 rounded-full">{t.status}</span>
          </div>
          <h2 className="text-lg font-black mt-1">{t.subject}</h2>
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="font-bold text-slate-700">Resident {t.initials || "?"}</span>
            <span>·</span>
            <span>
              {t.contact_kind === "email" && "📧 Email on file (masked)"}
              {t.contact_kind === "phone" && "📱 SMS on file (masked)"}
              {(!t.contact_kind || t.contact_kind === "none") && "🔗 No contact — reply via status link"}
            </span>
          </div>
          <div className="mt-2 text-[10px] text-slate-400 leading-snug">
            LaSalle privacy — student PII is not stored on this WO. Reply here and Field Radar will relay it to their masked contact.
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-6">
          <section>
            <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Details</h3>
            <div className="whitespace-pre-wrap text-sm text-slate-800 bg-slate-50 rounded-lg p-3">{t.description || "(none)"}</div>
            {t.priority_override && (
              <div className="mt-2 rounded-lg border-2 border-red-200 bg-red-50 p-3 text-xs" data-testid="chew-priority-override-banner">
                <div className="font-black text-red-700 flex items-center gap-1"><Warning size={13}/> Tenant escalated priority</div>
                <div className="text-red-800 mt-1">Bumped from <strong>{t.priority_override.from}</strong> → <strong>{t.priority_override.to}</strong></div>
                <div className="text-red-900 mt-1 italic">"{t.priority_override.reason}"</div>
              </div>
            )}
          </section>

          {t.photos?.length ? (
            <section>
              <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Photos ({t.photos.length})</h3>
              <div className="grid grid-cols-3 gap-2">
                {t.photos.map((p, i) => (
                  <a key={i} target="_blank" rel="noreferrer" href={`${BASE}/api/files/${encodeURIComponent(p.path)}`}
                     className="block aspect-square rounded-lg overflow-hidden bg-slate-100">
                    <img src={`${BASE}/api/files/${encodeURIComponent(p.path)}`} alt="" className="w-full h-full object-cover"/>
                  </a>
                ))}
              </div>
            </section>
          ) : null}

          {/* Permission link — only shown for admin-initiated permit tickets */}
          {t.permission_token && (
            <section className="rounded-2xl border-2 border-cyan-200 bg-cyan-50 p-4" data-testid="chew-permit-panel">
              <div className="flex items-center gap-2 text-cyan-800 font-black text-sm">
                <LockKey size={16}/> Entry-permission link
                {t.permission_granted && <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">Granted</span>}
                {!t.permission_granted && <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">Awaiting student</span>}
              </div>
              <div className="mt-2 flex items-center gap-2 bg-white rounded-lg p-2 border border-cyan-200">
                <div className="text-xs font-mono text-slate-700 truncate flex-1" data-testid="chew-permit-url">{t.permission_url}</div>
                <button onClick={copyPermitLink} className="bg-slate-900 hover:bg-slate-800 text-white text-[10px] font-black uppercase tracking-widest px-2 py-1.5 rounded flex items-center gap-1" data-testid="chew-permit-copy"><Copy size={12}/> Copy</button>
              </div>
              <div className="text-[11px] text-cyan-700 mt-2">Text or email this link to the student. They review the ticket and grant permission with a signed audit stamp.</div>

              {/* Admin proposed windows so far */}
              {(t.admin_proposed_windows || []).length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase font-black tracking-widest text-cyan-700 mb-1">You proposed</div>
                  <div className="space-y-1">
                    {t.admin_proposed_windows.map((w, i) => (
                      <div key={i} className="text-xs bg-white rounded p-2 border border-cyan-100" data-testid={`chew-admin-window-item-${i}`}>
                        <span className="font-bold">{w.label || `Window ${i+1}`}</span>
                        <span className="text-slate-600"> · {w.start} → {w.end}</span>
                        {w.note && <div className="text-[11px] italic text-slate-500">{w.note}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Propose form */}
              {!t.accepted_window && (
                <div className="mt-3 bg-white rounded-lg p-3 border border-cyan-200" data-testid="chew-admin-window-form">
                  <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Propose a window / set a hard appointment</div>
                  <div className="grid grid-cols-1 gap-2">
                    <input value={adminWin.label} onChange={e=>setAdminWin({...adminWin, label:e.target.value})}
                      placeholder="Label (e.g. Tue 2-4 PM window)"
                      className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="chew-admin-window-label"/>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={adminWin.start} onChange={e=>setAdminWin({...adminWin, start:e.target.value})}
                        placeholder="Start (e.g. Tue 2pm)"
                        className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="chew-admin-window-start"/>
                      <input value={adminWin.end} onChange={e=>setAdminWin({...adminWin, end:e.target.value})}
                        placeholder="End (e.g. Tue 4pm)"
                        className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="chew-admin-window-end"/>
                    </div>
                    <textarea value={adminWin.note} onChange={e=>setAdminWin({...adminWin, note:e.target.value})}
                      placeholder="Optional note to the student"
                      rows={2}
                      className="border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="chew-admin-window-note"/>
                    <label className="flex items-center gap-2 text-xs font-bold text-slate-700">
                      <input type="checkbox" checked={adminWin.hard_appointment}
                        onChange={e=>setAdminWin({...adminWin, hard_appointment:e.target.checked})}
                        data-testid="chew-admin-window-hard"/>
                      Hard appointment ("we're coming") — this schedules the visit immediately
                    </label>
                    <button disabled={busy} onClick={proposeAdminWindow}
                      className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white text-sm font-black py-2.5 rounded-lg"
                      data-testid="chew-admin-window-submit">
                      {adminWin.hard_appointment ? "Set hard appointment" : "Propose window"}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          <section>
            <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2 flex items-center gap-1"><Clock size={12}/> Tenant-proposed windows</h3>
            {t.accepted_window ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm">
                <div className="font-black text-emerald-800 flex items-center gap-2"><CheckCircle weight="fill" size={16}/> Scheduled</div>
                <div className="mt-1 font-bold">{t.accepted_window.label}</div>
                <div className="text-xs text-emerald-700">{t.accepted_window.start} → {t.accepted_window.end}</div>
                {t.accepted_window.note && <div className="text-xs text-emerald-700 mt-1"><em>Note: {t.accepted_window.note}</em></div>}
              </div>
            ) : (t.entry_windows?.length ? (
              <div className="space-y-2">
                {t.entry_windows.map((w, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3">
                    <div className="flex-1">
                      <div className="font-bold text-sm">{w.label || `Window ${i+1}`}</div>
                      <div className="text-xs text-slate-500">{w.start} → {w.end}</div>
                    </div>
                    <button disabled={busy} onClick={()=>acceptWindow(i)} className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-black px-3 py-2 rounded-lg disabled:opacity-50" data-testid={`chew-accept-window-${i}`}>Accept</button>
                  </div>
                ))}
              </div>
            ) : <div className="text-xs text-slate-400 italic">Tenant did not propose any windows.</div>)}
          </section>

          <section>
            <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2 flex items-center gap-1"><Envelope size={12}/> Notifications sent</h3>
            {t.notifications_sent?.length ? (
              <div className="text-xs text-slate-600 space-y-1">
                {t.notifications_sent.map((n,i)=>(<div key={i}>· {(n.role||"").toUpperCase()} — delivered {n.at ? new Date(n.at).toLocaleString() : ""}</div>))}
              </div>
            ) : <div className="text-xs text-slate-400 italic">No notifications sent yet.</div>}
          </section>

          {t.wo_id && (
            <button onClick={()=>nav(`/admin/jobs/${t.wo_id}`)} className="w-full bg-slate-900 text-white rounded-xl font-black py-3 text-sm flex items-center justify-center gap-2" data-testid="chew-open-wo-btn">
              Open work order <ArrowRight size={16}/>
            </button>
          )}

          <section>
            <h3 className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Messages</h3>
            <div className="space-y-2 mb-3">
              {(t.messages || []).map((m, i) => (
                <div key={i} className={`text-sm rounded-lg p-2.5 ${m.from === "tenant" ? "bg-slate-100 mr-8" : "bg-cyan-50 ml-8 text-cyan-900"}`}>
                  <div className="text-[10px] uppercase font-black tracking-wider opacity-60">{m.from}{m.author ? ` · ${m.author}` : ""}</div>
                  <div>{m.text}</div>
                </div>
              ))}
              {!(t.messages || []).length && <div className="text-xs text-slate-400 italic">No messages yet.</div>}
            </div>
            <div className="flex gap-2">
              <input value={msgText} onChange={e=>setMsgText(e.target.value)} placeholder="Reply to tenant…"
                     className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="chew-msg-input"/>
              <button disabled={busy || !msgText.trim()} onClick={sendMsg} className="bg-slate-900 text-white rounded-lg px-4 disabled:opacity-40" data-testid="chew-msg-send"><PaperPlaneTilt weight="fill" size={16}/></button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function NewPermitModal({ onClose, onCreated }) {
  const [unit, setUnit] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { permission_url, ref_code }

  async function create() {
    if (!unit.trim() || !subject.trim()) { toast.error("Unit and subject are required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/chew/hub/entry-permission", { unit, subject, description, priority });
      setResult(r.data);
      onCreated && onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not create link")); }
    finally { setBusy(false); }
  }

  async function copy() {
    if (!result?.permission_url) return;
    try {
      await navigator.clipboard.writeText(result.permission_url);
      toast.success("Copied — paste into your SMS or email");
    } catch { toast.error("Copy failed"); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={e=>e.stopPropagation()} data-testid="chew-new-permit-modal">
        <h3 className="text-lg font-black mb-1">New entry-permission link</h3>
        <p className="text-xs text-slate-500 mb-4">Create a ticket + a private link you can text or email to a student to authorize entry. No student PII stored.</p>
        {!result && (
          <div className="space-y-3">
            <label className="block">
              <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Unit</div>
              <input value={unit} onChange={e=>setUnit(e.target.value)} placeholder="e.g. 2A"
                     className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="chew-new-permit-unit"/>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Subject</div>
              <input value={subject} onChange={e=>setSubject(e.target.value)} placeholder="e.g. Kitchen faucet replacement"
                     className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="chew-new-permit-subject"/>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Description</div>
              <textarea value={description} onChange={e=>setDescription(e.target.value)} rows={3}
                        placeholder="What we'll be doing…"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="chew-new-permit-desc"/>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">Priority</div>
              <select value={priority} onChange={e=>setPriority(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="chew-new-permit-priority">
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>
            <div className="flex gap-2 mt-4">
              <button onClick={onClose} className="flex-1 bg-slate-100 rounded-lg py-2.5 font-bold text-sm">Cancel</button>
              <button onClick={create} disabled={busy} className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg py-2.5 font-black text-sm disabled:opacity-50" data-testid="chew-new-permit-create">
                {busy ? "Creating…" : "Create link"}
              </button>
            </div>
          </div>
        )}
        {result && (
          <div className="space-y-3" data-testid="chew-new-permit-result">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
              <div className="flex items-center gap-2 text-emerald-800 font-black text-sm"><CheckCircle weight="fill" size={16}/> Link ready</div>
              <div className="text-xs text-emerald-700 mt-1">Ref <span className="font-mono">{result.ref_code}</span></div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-1">Shareable link</div>
              <div className="text-xs font-mono text-slate-700 break-all" data-testid="chew-new-permit-url">{result.permission_url}</div>
              <button onClick={copy} className="mt-2 bg-slate-900 text-white text-xs font-black px-3 py-2 rounded-lg flex items-center gap-1" data-testid="chew-new-permit-copy"><Copy size={12}/> Copy link</button>
            </div>
            <button onClick={onClose} className="w-full bg-slate-100 rounded-lg py-2.5 font-bold text-sm">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

function NotifyConfigModal({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/chew/hub/notify-config").then(r => setCfg(r.data)).catch(()=>toast.error("Could not load config"));
  }, []);
  async function save() {
    setSaving(true);
    try { await api.post("/chew/hub/notify-config", cfg); toast.success("Saved"); onClose(); }
    catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e=>e.stopPropagation()} data-testid="chew-notify-config-modal">
        <h3 className="text-lg font-black mb-1">Chew Flats notifications</h3>
        <p className="text-xs text-slate-500 mb-4">These emails receive every new maintenance request. Leave any blank to skip.</p>
        {cfg ? (
          <div className="space-y-3">
            <ConfigField label="PMR (you)" value={cfg.pmr_email} onChange={v=>setCfg({...cfg, pmr_email:v})} testId="notify-pmr"/>
            <ConfigField label="ProCall" value={cfg.procall_email} onChange={v=>setCfg({...cfg, procall_email:v})} testId="notify-procall"/>
            <ConfigField label="LaSalle" value={cfg.lasalle_email} onChange={v=>setCfg({...cfg, lasalle_email:v})} testId="notify-lasalle"/>
            <ConfigField label="Owner" value={cfg.owner_email} onChange={v=>setCfg({...cfg, owner_email:v})} testId="notify-owner"/>
            <div className="flex gap-2 mt-4">
              <button onClick={onClose} className="flex-1 bg-slate-100 rounded-lg py-2.5 font-bold text-sm">Cancel</button>
              <button onClick={save} disabled={saving} className="flex-1 bg-slate-900 text-white rounded-lg py-2.5 font-black text-sm disabled:opacity-50" data-testid="chew-notify-save-btn">{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        ) : <div className="text-sm text-slate-400">Loading…</div>}
      </div>
    </div>
  );
}

function ConfigField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">{label}</div>
      <input type="email" value={value || ""} onChange={e=>onChange(e.target.value)}
             className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid={testId}/>
    </label>
  );
}
